import { EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useHomeworkState from "../../state/homework.state";
import { getElapsedFromStart, maxCharactersFor20CPS } from "../../utils/common";
import { Cue } from "../../utils/vtt";
import CategoryManager, { CategoryMangerHandle } from "../CategoryManager";

type Props = {
  onSubmit: (values: Cue) => void;
  defaultValue?: Cue;
  buttonText?: string;
  isDisabled?: boolean;
  validateCue: (
    startTime: string,
    endTime: string,
    type: "captions" | "subtitles",
  ) => string | undefined;
};

export default function CueForm({
  onSubmit,
  defaultValue,
  buttonText,
  isDisabled,
  validateCue = () => undefined,
}: Props) {
  const { t } = useTranslation();
  const categoryManagerRef = useRef<CategoryMangerHandle>(null);
  const { categories } = useHomeworkState();
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid, isSubmitted },
  } = useForm<Cue>();

  useEffect(() => {
    // The reset doesn't work as expect with the `Select` component so `setValue` is used instead
    setValue("startTime", defaultValue?.startTime ?? "");
    setValue("endTime", defaultValue?.endTime ?? "");
    setValue("text", defaultValue?.text ?? "");
    setValue("class", defaultValue?.class);
    setValue("type", defaultValue?.type ?? "subtitles");
  }, [defaultValue, setValue]);

  const _onSubmit = useCallback(
    (cue: Cue) => {
      handleSubmit(() => onSubmit(cue))().then(() =>
        reset({
          startTime: "",
          endTime: "",
          text: "",
          class: "",
          type: "subtitles",
        }),
      );
    },
    [handleSubmit, onSubmit, reset],
  );

  const validateEndTime = useCallback(
    (endTime: string) => {
      if (
        getElapsedFromStart(endTime) <=
        getElapsedFromStart(getValues("startTime"))
      ) {
        return t("forms.validation.must_be_less", {
          param1: t("forms.start"),
          param2: t("forms.end"),
        });
      }

      return validateCue(getValues("startTime"), endTime, getValues("type"));
    },
    [getValues, t, validateCue],
  );

  return (
    <>
      <form onSubmit={handleSubmit(_onSubmit)}>
        <FormControl
          isDisabled={isDisabled}
          isInvalid={!isValid && isSubmitted}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <HStack alignItems="flex-start">
            <Box flexGrow={1}>
              <FormLabel htmlFor="title">{t("forms.start")}</FormLabel>
              <Input
                id="startTime"
                type="time"
                step="1"
                placeholder="hh:mm:ss"
                {...register("startTime", {
                  required: t("forms.validation.required"),
                  pattern: {
                    value: /^(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)$/,
                    message: `${t("forms.validation.invalid_format")} (hh:mm:ss)`,
                  },
                })}
              />
              <FormErrorMessage>
                {errors.startTime && errors.startTime.message}
              </FormErrorMessage>
            </Box>
            <Box flexGrow={1}>
              <FormLabel htmlFor="title">{t("forms.end")}</FormLabel>
              <Input
                id="endTime"
                type="time"
                step="1"
                placeholder="hh:mm:ss"
                {...register("endTime", {
                  required: t("forms.validation.required"),
                  pattern: {
                    value: /^(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)$/,
                    message: `${t("forms.validation.invalid_format")} (hh:mm:ss)`,
                  },
                  validate: validateEndTime,
                })}
              />
              <FormErrorMessage>
                {errors.endTime && errors.endTime.message}
              </FormErrorMessage>
            </Box>
          </HStack>
          <div>
            <Box flexGrow={1}>
              <FormLabel htmlFor="type">{t("forms.type")}</FormLabel>
              <Select
                id="type"
                {...register("type", {
                  required: t("forms.validation.required"),
                })}
              >
                {[
                  { id: "subtitles", name: t("common.labels.subtitle") },
                  { id: "captions", name: t("common.labels.caption") },
                ].map((cueType) => (
                  <option key={cueType.id} value={cueType.id}>
                    {cueType.name}
                  </option>
                ))}
              </Select>
            </Box>
            <FormErrorMessage>
              {errors.type && errors.type.message}
            </FormErrorMessage>
          </div>
          <div>
            <HStack alignItems="flex-end">
              <Box flexGrow={1}>
                <FormLabel htmlFor="category">{t("forms.category")}</FormLabel>
                <Select
                  placeholder={t("forms.select_category")}
                  id="category"
                  {...register("class")}
                >
                  {Object.values(categories).map((category) => (
                    <option
                      key={`${category.uuid}${category.name}`}
                      style={{ color: category.color }}
                      value={category.uuid}
                    >
                      {category.name}
                    </option>
                  ))}
                </Select>
              </Box>
              <IconButton
                isDisabled={isDisabled}
                onClick={() => categoryManagerRef.current?.onOpen()}
                aria-label="Edit categories"
                fontSize="18px"
                icon={<EditIcon />}
              />
            </HStack>
            <FormErrorMessage>
              {errors.class && errors.class.message}
            </FormErrorMessage>
          </div>
          <Box display="flex" flexDirection="column">
            <FormLabel htmlFor="text">{t("forms.text")}</FormLabel>
            <Textarea
              rows={3}
              id="text"
              disabled={!watch("startTime") || !watch("endTime")}
              placeholder={t("forms.text")}
              maxLength={maxCharactersFor20CPS(
                watch("startTime"),
                watch("endTime"),
              )}
              {...register("text", {
                required: t("forms.validation.required"),
              })}
            />
            <Text alignSelf="flex-end" mt={2} fontSize={14} fontStyle="italic">
              {t("common.labels.used_characters")}:&nbsp;
              {watch("text")?.length ?? 0}/
              {maxCharactersFor20CPS(watch("startTime"), watch("endTime"))}
            </Text>
            <FormErrorMessage>
              {errors.text && errors.text.message}
            </FormErrorMessage>
          </Box>
        </FormControl>
        <Button
          isDisabled={isDisabled}
          mt={4}
          bg="primary.DEFAULT"
          color="white"
          type="submit"
        >
          {buttonText ?? t("common.buttons.new")}
        </Button>
      </form>
      <CategoryManager ref={categoryManagerRef} />
    </>
  );
}
